/**
 * Los `warnings` del render, resumidos en UN aviso.
 *
 * El motor devuelve un warning **por panel** (`{panel_id, code, message}`), y ese detalle ya se ve
 * en cada bloque. Lo que faltaba era arriba: hasta la card `4By6KR9g` el frontend **no consumía
 * `warnings` en ningún lado** —el backend lo venía calculando y viajando para nadie—, así que un
 * informe con las doce consultas caídas por una cuenta desconectada mostraba doce cajas rojas y
 * **ninguna acción**: el usuario leía qué pasó y no tenía a dónde ir.
 *
 * Agrupar es el punto: sin esto, el aviso sería el mismo párrafo doce veces en la franja en vez de
 * en los paneles, que es el ruido que se quería evitar.
 */

/**
 * Prioridad cuando un render trae códigos MEZCLADOS — pasa, porque cada grupo de query falla por su
 * cuenta. Primero lo que el usuario puede ARREGLAR, después lo que sólo hay que ESPERAR, al final
 * lo genérico: mandar a esperar cuando además hay una cuenta caída es el bug original de la card,
 * servido desde otro ángulo.
 */
export const WARNING_PRIORITY = [
    'NO_CONECTION_TO_ACCOUNT',
    'GA4_NO_ACCESS',
    'NO_ACCESS',
    'API_KEY_INVALID',
    'GA4_TOO_MANY_FIELDS',
    'GA4_INCOMPATIBLE_FIELDS',
    'INVALID_QUERY',
    'DATA_PREPARING',
    'DATA_NODE_ERROR',
    'QUERY_ERROR',
];

/** Códigos con un copy propio en `informes.warnings.*`. El resto cae en `GENERIC`. */
export const WARNING_COPY_CODES = new Set([
    'NO_CONECTION_TO_ACCOUNT', 'GA4_NO_ACCESS', 'DATA_PREPARING',
]);

/**
 * Códigos accionables por la agencia: son los que llevan el botón a Conexiones.
 *
 * ⚠️ `NO_ACCESS` NO está, aunque su nombre suene a cuenta inaccesible: es lo que responde el **403
 * del gate de módulo de la SUSCRIPCIÓN** (`{'error_message': 'NO_ACCESS'}`). Mandaba a
 * `/client/<id>/conections` por un problema de plan — la misma conflación plan↔conexión que esta
 * card sacó del Explorador, reintroducida en el aviso (review R-reports-fe-04). Y como tampoco
 * está en `WARNING_COPY_CODES`, su `copyCode` caía en `GENERIC`, cuyo `.cta` no existe en ningún
 * bundle: el botón salía con el `defaultValue` en español duro incluso con la UI en inglés.
 *
 * Todo código que entre acá tiene que estar TAMBIÉN en `WARNING_COPY_CODES` y tener su `.cta` en
 * los dos bundles. Lo exige `panelErrorCopy.test.js`.
 */
export const WARNING_CTA_CODES = new Set(['NO_CONECTION_TO_ACCOUNT', 'GA4_NO_ACCESS']);

/**
 * ¿El informe quedó sin un solo bloque con datos? Es lo que decide si se puede bajar el PDF.
 *
 * Vive acá y no inline en la página porque el defecto que esto arregla NO estaba en el botón —que
 * ya sabía deshabilitarse— sino en el cálculo que lo alimentaba: `!!error || !result` sólo cubre
 * «la llamada falló», y el caso central de la card devuelve 200 con todos los paneles en ERROR.
 * Como función suelta se puede ejercer sin montar la pantalla entera, que es la única forma de que
 * un test cubra la capa donde el defecto ocurrió (QA `QA-VIS-err-04`).
 *
 * @param error el error de transporte del render, si lo hubo.
 * @param renderResult la respuesta del motor.
 * @param hasText si la hoja tiene algún bloque de texto con contenido. Un texto no pasa por el
 *        motor (no viene en `renderResult`), pero sí sale en el PDF: un informe hecho sólo de
 *        textos —o uno con los datos caídos y un texto escrito— tiene algo para bajar.
 */
export function reportHasNothingToExport(error, renderResult, {hasText = false} = {}) {
    if (hasText) return false;
    if (error || !renderResult) return true;
    const summary = consolidateWarnings(renderResult);
    return !!(summary && summary.everyPanelFailed);
}

/**
 * @param renderResult la respuesta del motor.
 * @returns `{code, copyCode, affected, total, allPanels, everyPanelFailed, showScope}` o `null` si
 *          no hay nada que avisar. `code` es el real; `copyCode` es contra el que se buscan las
 *          claves de i18n. `allPanels` habla de ESTE aviso; `everyPanelFailed`, del informe.
 */
export function consolidateWarnings(renderResult) {
    const warnings = (renderResult && renderResult.warnings) || [];
    if (!warnings.length) return null;
    const total = ((renderResult && renderResult.panels) || []).length;

    const byCode = {};
    warnings.forEach((warning) => {
        const code = (warning && warning.code) || 'QUERY_ERROR';
        byCode[code] = (byCode[code] || 0) + 1;
    });

    const codes = Object.keys(byCode);
    // Un código que no está en la lista de prioridad no puede ganarle a uno que sí: `indexOf` da
    // -1 y quedaría primero.
    const rank = (code) => {
        const index = WARNING_PRIORITY.indexOf(code);
        return index === -1 ? WARNING_PRIORITY.length : index;
    };
    const code = codes.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))[0];

    const affected = byCode[code];
    return {
        code,
        copyCode: WARNING_COPY_CODES.has(code) ? code : 'GENERIC',
        affected,
        total,
        // ⚠️ DOS preguntas distintas, dos predicados. Hasta el 2026-09-20 había uno solo
        // (`warnings.length >= total`) y contestaba mal la primera: con 6 paneles caídos por una
        // cuenta y 1 por otra causa, el aviso decía «Afecta a todo el informe» cuando explicaba 6
        // de 7 — y el comentario que estaba acá afirmaba justo lo contrario de lo que el código
        // hacía (QA `QA-err-07`).
        //
        // `allPanels`: ¿los paneles que ESTE aviso explica son todos los del informe? Es lo que
        // decide la frase de alcance, así que se mide contra `affected`, no contra el total de
        // fallados.
        allPanels: total > 0 && affected >= total,
        // `everyPanelFailed`: ¿falló TODO el informe, por la causa que sea? No dice nada del aviso
        // — dice si quedó algún bloque con datos. Lo usa el botón de PDF, que no puede mirar
        // `allPanels`: con dos causas ninguna alcanza sola y el PDF saldría habilitado sobre un
        // informe entero en «No disponible» (QA `QA-VIS-err-04`).
        everyPanelFailed: total > 0 && warnings.length >= total,
        // Sin paneles no hay alcance que contar. El payload existe en el contrato:
        // `client_report_service.render_for_portal` devuelve `{'panels': [], 'warnings':
        // [{'code': 'SNAPSHOT_NOT_READY'}]}` cuando la foto todavía no se generó, y la frase salía
        // «Afecta a 1 de 0 bloques» (review R-reports-fe-12).
        showScope: total > 0,
    };
}
